<template>
  <div>
    <!--Breadcrumb button-->
    <breadcrumb-button hide-settings>
      <template #beforeDropdown>

        <b-button variant="primary" class="btn-icon mx-1" v-b-tooltip v-bind:title="$t('columns.self')"
                  @click="openColumnsModal">
          <i class="fa-solid fa-bars"></i>
        </b-button>
      </template>

    </breadcrumb-button>
    <!--Registration-time range: preset pills + custom picker (replaces the
        registrationTime field that used to sit inside the filter card)-->
    <calendar-bar @input="onDateRange"/>
    <!--Filters-->
    <filter-card v-model="filter" v-bind:fields="filterFields" search file-export @export="createExcelFile"
                 v-bind:hidden-fields="{ registrationTime: true }"/>

    <!--Totals strip: deposits / withdrawals / net (always visible) -->
    <b-card no-body class="mb-1">
      <b-card-body class="monetary-totals">
        <div class="sum-chip sum-chip--deposit">
          <span class="sum-chip__icon"><i class="fa-solid fa-arrow-down"></i></span>
          <span class="sum-chip__body">
            <span class="sum-chip__label">{{ $t('monetary.totalDeposits') }} · {{ totalDeposits }}</span>
            <span class="sum-chip__value">{{ convertToEURO(depositsSum) }}</span>
          </span>
        </div>
        <div class="sum-chip sum-chip--withdraw">
          <span class="sum-chip__icon"><i class="fa-solid fa-arrow-up"></i></span>
          <span class="sum-chip__body">
            <span class="sum-chip__label">{{ $t('monetary.totalWithdrawals') }} · {{ totalWithdrawals }}</span>
            <span class="sum-chip__value">{{ convertToEURO(withdrawalsSum) }}</span>
          </span>
        </div>
        <div class="sum-chip sum-chip--all">
          <span class="sum-chip__icon"><i class="fa-solid fa-scale-balanced"></i></span>
          <span class="sum-chip__body">
            <span class="sum-chip__label">{{ $t('monetary.netTotal') }}</span>
            <span class="sum-chip__value">{{ convertToEURO(netSum) }}</span>
          </span>
        </div>
      </b-card-body>
    </b-card>

    <!--Deposits table-->
    <b-card no-body>
      <b-card-header class="pb-50 monetary-toolbar">
        <div class="monetary-toolbar__info">
          <h5 class="mb-0 mr-1">{{ $t('monetary.deposits') }}</h5>
          <span class="text-muted">{{ totalDeposits }} {{ $t('menu.monetaries') }}</span>
          <!-- Temporary STD-bulk fixer — own permission, separate from the regular bulk actions -->
          <b-button v-if="hasPermission(permissions.MONETARY_STD_BULK)" size="sm" variant="outline-warning"
                    class="ml-1" @click="$refs.stdBulkModal.open(stdFixScope)">
            <i class="fa-solid fa-list-check mr-25"></i>{{ $t('stdBulk.button') }}
          </b-button>
        </div>
        <div class="sum-chip sum-chip--deposit">
          <span class="sum-chip__icon"><i class="fa-solid fa-coins"></i></span>
          <span class="sum-chip__body">
            <span class="sum-chip__label">{{ $t('monetary.totalDeposits') }}</span>
            <span class="sum-chip__value">{{ convertToEURO(depositsSum) }}</span>
          </span>
        </div>
      </b-card-header>
      <b-card-body>
        <monetary-table ref="depositsTable" v-bind:filters="depositsFilters" v-bind:columns="columns"
                        :has-selection="true" @selected="onMonetariesSelected" :lazy="false" :show-duplicated="false"
                        trans-type="deposits" @total="total=>totalDeposits=total" @sum="val=>depositsSum=val">
          <template #table-top-actions="{ selectedIds }">
            <div class="mv-bulk" v-if="Object.keys(selectedIds).length">
              <span class="mv-bulk__count">
                <i class="fa-solid fa-check-double mr-25"></i>
                {{ Object.keys(selectedIds).length }} {{ $t('finance.actionModal.selected') }}
              </span>
              <b-button size="sm" variant="primary" @click="openBulkActions('depositsTable')">
                <i class="fa-solid fa-sliders mr-25"></i>{{ $t('finance.actions') }}
              </b-button>
              <b-button size="sm" variant="flat-secondary" @click="$refs.depositsTable.clearSelection()">
                {{ $t('operations.cancel') }}
              </b-button>
            </div>
          </template>
        </monetary-table>
      </b-card-body>
    </b-card>

    <!--Withdrawals table-->
    <b-card no-body class="mt-1">
      <b-card-header class="pb-50 monetary-toolbar">
        <div class="monetary-toolbar__info">
          <h5 class="mb-0 mr-1">{{ $t('monetary.withdrawals') }}</h5>
          <span class="text-muted">{{ totalWithdrawals }} {{ $t('menu.monetaries') }}</span>
        </div>
        <div class="sum-chip sum-chip--withdraw">
          <span class="sum-chip__icon"><i class="fa-solid fa-coins"></i></span>
          <span class="sum-chip__body">
            <span class="sum-chip__label">{{ $t('monetary.totalWithdrawals') }}</span>
            <span class="sum-chip__value">{{ convertToEURO(withdrawalsSum) }}</span>
          </span>
        </div>
      </b-card-header>
      <b-card-body>
        <monetary-table ref="withdrawalsTable" v-bind:filters="withdrawalsFilters" v-bind:columns="columns"
                        :has-selection="true" @selected="onMonetariesSelected" :lazy="false" :show-duplicated="false"
                        trans-type="withdrawals" @total="total=>totalWithdrawals=total" @sum="val=>withdrawalsSum=val">
          <template #table-top-actions="{ selectedIds }">
            <div class="mv-bulk" v-if="Object.keys(selectedIds).length">
              <span class="mv-bulk__count">
                <i class="fa-solid fa-check-double mr-25"></i>
                {{ Object.keys(selectedIds).length }} {{ $t('finance.actionModal.selected') }}
              </span>
              <b-button size="sm" variant="primary" @click="openBulkActions('withdrawalsTable')">
                <i class="fa-solid fa-sliders mr-25"></i>{{ $t('finance.actions') }}
              </b-button>
              <b-button size="sm" variant="flat-secondary" @click="$refs.withdrawalsTable.clearSelection()">
                {{ $t('operations.cancel') }}
              </b-button>
            </div>
          </template>
        </monetary-table>
      </b-card-body>
    </b-card>

    <!--Modal-->
    <columns-modal ref="columns-modal" column-key="monetaries" v-model="columns"
                   v-bind:columns-names="columnsNames"
    />
    <monetaries-modal ref="monetaries-modal"/>
    <monetary-action-modal ref="bulkActions" @done="onBulkDone"/>
    <std-bulk-fix-modal ref="stdBulkModal" @done="refreshTables"/>
  </div>
</template>

<script>

import { mapActions, mapGetters } from 'vuex';
import BreadcrumbButton from '@/components/widget/breadcrumbButton';
import MonetariesSearchRequest from '@/model/monetary/MonetarySearchRequest';
import ColumnsModal from '@/components/modal/columnsModal';
import Monetaries from '@/model/monetary/Monetary';
import mixinPermissions from '@/components/mixin/mixinPermissions';
import mixinBase from '@/components/mixin/mixinBase';
import mixinNotifications from '@/components/mixin/mixinNotifications';
import MonetariesModal from '@/components/modal/monetaryModal';
import MonetaryActionModal from '@/components/modal/MonetaryActionModal';
import StdBulkFixModal from '@/components/modal/StdBulkFixModal';
import FilterCard from '@/components/widget/FilterCard';
import MonetariesFilterFields from '@/model/filter/MonetaryFilterFields';
import MonetaryTable from '@/components/tables/MonetariesTable';
import CalendarBar from '@/components/widget/CalendarBar';

export default {
  name: 'MonetaryView',
  components: {
    MonetaryTable,
    FilterCard,
    MonetariesModal,
    MonetaryActionModal,
    StdBulkFixModal,
    ColumnsModal,
    BreadcrumbButton,
    CalendarBar,
  },
  mixins: [mixinBase, mixinPermissions, mixinNotifications],
  data() {
    return {
      totalDeposits: 0,
      totalWithdrawals: 0,
      depositsSum: 0,
      withdrawalsSum: 0,
      columnsNames: [
          ...Object.keys(Monetaries()),
        "clientDetails.accountId", "clientDetails.clientId", "clientDetails.email", "clientDetails.country", "clientDetails.affiliate",
        'clientDetails.clientUsers.representativeId',
        'paymentDetails.processorName', 'paymentDetails.comments', 'clientComment', 'actions'
      ],
      filter: MonetariesSearchRequest(),
      filterLoaded: false,
      columns: [],
      filterFields: null,
      // Which table's selection opened the bulk-actions modal
      bulkSourceRef: null,
    };
  },
  watch: {
    // Default is EXCLUDE test: a cleared/reset filter falls back to false so
    // test transactions stay hidden until the box is ticked. $set — a filter
    // object restored from a SAVED query may predate the key, and a plain
    // assignment on it would not be reactive (checkbox would look dead).
    'filter.isTest'(val) {
      if (val == null) {
        this.$set(this.filter, 'isTest', false);
      }
    },
    // Same for TXF (taxes) rows: hidden unless "Include TXF" is ticked.
    'filter.isTxf'(val) {
      if (val == null) {
        this.$set(this.filter, 'isTxf', false);
      }
    },
    // A loaded/reset filter REPLACES the whole object — re-apply the defaults
    // so isTest/isTxf stay present (and reactive) on the new object.
    filter(newFilter) {
      if (newFilter && newFilter.isTest == null) {
        this.$set(newFilter, 'isTest', false);
      }
      if (newFilter && newFilter.isTxf == null) {
        this.$set(newFilter, 'isTxf', false);
      }
    },
  },
  async created() {
    // Test transactions are excluded by default; ticking the "Include test"
    // checkbox ADDS them to the normal (non-test) results instead of replacing.
    this.$set(this.filter, 'isTest', false);
    // TXF (taxes) rows are likewise hidden until "Include TXF" is ticked.
    this.$set(this.filter, 'isTxf', false);
    await this.refreshData();
    this.filterFields = MonetariesFilterFields();
  },
  computed: {
    ...mapGetters('data', ['allOrganizations']),
    // Both tables share the page filter; only the transaction type differs.
    depositsFilters() {
      return { ...this.filter, type: 'DEPOSIT' };
    },
    // The STD-bulk preview obeys the page filter: orgs / business units /
    // depositors / registration-time range. The backend keeps each listed
    // client's candidate list complete — the filter only picks WHO appears.
    stdFixScope() {
      const f = this.filter || {};
      const rt = f.registrationTime || {};
      return {
        orgIds: f.orgIds && f.orgIds.length ? f.orgIds : null,
        buIds: f.businessUnit && f.businessUnit.length ? f.businessUnit : null,
        userIds: f.userIds && f.userIds.length ? f.userIds : null,
        range: {
          from: rt.start ? this.$moment(rt.start).format('YYYY-MM-DD') : null,
          until: rt.end ? this.$moment(rt.end).format('YYYY-MM-DD') : null,
        },
      };
    },
    withdrawalsFilters() {
      return { ...this.filter, type: 'WITHDRAWAL' };
    },
    netSum() {
      // Withdrawal amounts are stored negative — subtract their absolute value
      // so the net stays "deposits minus withdrawals" whatever the sign.
      return (this.depositsSum || 0) - Math.abs(this.withdrawalsSum || 0);
    },
  },
  methods: {
    ...mapActions('monetary', ['refreshData', 'exportMonetaryDataExcel']),

    // ---- Bulk actions: finance-style modal (comment / approve / decline /
    // fake approve / delete), selection mechanics like the finance dashboard ----
    openBulkActions(refName) {
      const table = this.$refs[refName];
      const items = table ? table.getAllSelectedItems() : [];
      if (!items.length) return;
      this.bulkSourceRef = refName;
      this.$refs.bulkActions.open(items);
    },
    onBulkDone() {
      const table = this.$refs[this.bulkSourceRef];
      if (table) {
        table.clearSelection();
      }
      this.refreshTables();
    },
    // Deposits and withdrawals share the same pool — refresh both.
    refreshTables() {
      if (this.$refs.depositsTable) this.$refs.depositsTable.refresh();
      if (this.$refs.withdrawalsTable) this.$refs.withdrawalsTable.refresh();
    },
    // CalendarBar → registrationTime filter for both tables (start of the
    // "from" day until end of the "until" day, server-local time).
    onDateRange(range) {
      if (!range) return;
      this.filter.registrationTime = {
        start: range.from ? this.$moment(range.from).startOf('day').toServerDateTimeFormat() : null,
        end: range.until ? this.$moment(range.until).endOf('day').toServerDateTimeFormat() : null,
      };
    },
    openColumnsModal() {
      this.$refs['columns-modal'].showModal();
    },
    onMonetariesSelected(selectedId) {
      this.$refs['monetaries-modal'].showModal(selectedId);
    },
    createExcelFile(){
      // Export ALL transaction types matching the filters (both tables).
      this.exportMonetaryDataExcel({ filter: { ...this.filter, type: null } });
    },
  }
};
</script>

<style>
.style-chooser .vs__search::placeholder,
.style-chooser .vs__dropdown-toggle,
.style-chooser .vs__dropdown-menu {
  background: #dfe5fb;
  border: none;
  color: #394066;
  text-transform: lowercase;
  font-variant: small-caps;
  height: 10px;
}

/* ── Header toolbar: count + amount reminder ── */
.monetary-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
}
.monetary-toolbar__info {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.65rem;
}

/* Totals strip above the two tables */
.monetary-totals {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.85rem;
  padding: 0.85rem 1.25rem;
}

/* Compact KPI-style chip (mini version of the monetary-dashboard cards) */
.sum-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.3rem 0.75rem 0.3rem 0.4rem;
  border-radius: 12px;
  color: #fff;
  box-shadow: 0 6px 16px -8px rgba(20, 24, 50, 0.45);
}
.sum-chip__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 9px;
  background: rgba(255, 255, 255, 0.22);
  font-size: 0.85rem;
  flex-shrink: 0;
}
.sum-chip__body {
  display: flex;
  flex-direction: column;
  line-height: 1.1;
}
.sum-chip__label {
  font-size: 0.6rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  opacity: 0.9;
}
.sum-chip__value {
  font-size: 0.95rem;
  font-weight: 800;
  letter-spacing: -0.01em;
}
.sum-chip--deposit { background: linear-gradient(135deg, #34d399 0%, #10b981 60%, #059669 100%); }
.sum-chip--withdraw { background: linear-gradient(135deg, #fb7185 0%, #ef4444 60%, #c1272d 100%); }
.sum-chip--all { background: linear-gradient(135deg, #4da3ff 0%, #007BFF 60%, #0062cc 100%); }

/* Bulk-selection bar above the tables (mirrors the finance dashboard's) */
.mv-bulk {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.3rem 0.75rem;
  border-radius: 10px;
  background: rgba(0, 123, 255, 0.08);
  margin-bottom: 0.5rem;
}
.mv-bulk__count {
  font-weight: 700;
  font-size: 0.85rem;
}
</style>
